import * as React from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { cva, VariantProps } from 'class-variance-authority';

import { cn } from '../../utils/cn';
import { DialogOverlay } from './dialog-overlay';
import { DialogPortal } from './dialog-portal';
import { DialogClose } from './dialog-close';

const dialogContentVariants = cva('rocco-dialog-content z-modal', {
    variants: {
        variant: {
            default:
                'fixed left-[50%] top-[50%] grid w-full max-w-lg translate-x-[-50%] translate-y-[-50%] gap-4 border bg-background shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] sm:rounded-lg',
            fullScreen:
                'fixed left-0 top-0 grid h-full w-full translate-x-0 translate-y-0 gap-4 border bg-background shadow-lg duration-200 data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 sm:rounded-lg',
            pdfViewer:
                'fixed left-0 top-0 grid h-full w-full translate-x-0 translate-y-0 gap-4 bg-[#3D3938] duration-200 data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95',
        },
        spacing: {
            none: 'p-0',
            sm: 'p-4',
            md: 'p-6',
            lg: 'p-8',
            xl: 'p-10',
            '2xl': 'p-12',
            '3xl': 'p-14',
            '4xl': 'p-16',
        },
    },
    defaultVariants: {
        variant: 'default',
        spacing: 'md',
    },
});

const hideCloseButton = (
    variant: 'default' | 'fullScreen' | 'pdfViewer' | null | undefined,
    closeButton?: boolean
) => {
    // If closeButton is explicitly set to false, hide the button
    if (closeButton === false) {
        return true;
    }
    // Otherwise, use the variant-based logic
    return variant === 'pdfViewer';
};

export const DialogContent = React.forwardRef<
    React.ElementRef<typeof DialogPrimitive.Content>,
    React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content> &
        VariantProps<typeof dialogContentVariants> & {
            closeButton?: boolean;
        }
>(({ className, children, variant, spacing, closeButton, ...props }, ref) => {
    return (
        <DialogPortal>
            <DialogOverlay />
            <DialogPrimitive.Content
                ref={ref}
                className={cn(
                    dialogContentVariants({ variant, spacing, className })
                )}
                {...props}
            >
                {children}
                {!hideCloseButton(variant, closeButton) && <DialogClose />}
            </DialogPrimitive.Content>
        </DialogPortal>
    );
});

DialogContent.displayName = DialogPrimitive.Content.displayName;
